import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SliceName } from '@/shared/config';

import {
  DocumentTemplate,
  DocumentTemplateSummary,
  EncounterSchema,
  SchemaDocumentRef,
  VersionStatus,
} from '../config/types';

import {
  fetchDocumentTemplates,
  fetchDeletedDocumentTemplates,
  fetchDocumentTemplateById,
  createDocumentTemplateVersion,
  updateDocumentTemplateDraft,
  deleteDocumentTemplateThunk,
  restoreDocumentTemplateThunk,
  fetchEncounterSchemas,
  fetchEncounterSchemaById,
  createEncounterSchemaThunk,
  updateEncounterSchemaThunk,
  deleteEncounterSchemaThunk,
  restoreEncounterSchemaThunk,
} from './templatesSlice.thunks';

type TemplatesState = {
  documentTemplateSummaries: DocumentTemplateSummary[];
  documentTemplatesLoading: boolean;
  documentTemplateById: Record<string, DocumentTemplate>;
  documentTemplateByIdLoading: Record<string, boolean>;
  encounterSchemas: EncounterSchema[];
  encounterSchemasLoading: boolean;
  encounterSchemasListError: string | null;
  error: string | null;
};

const initialState: TemplatesState = {
  documentTemplateSummaries: [],
  documentTemplatesLoading: false,
  documentTemplateById: {},
  documentTemplateByIdLoading: {},
  encounterSchemas: [],
  encounterSchemasLoading: false,
  encounterSchemasListError: null,
  error: null,
};

const toSummary = (template: DocumentTemplate): DocumentTemplateSummary => ({
  id: template.id,
  series_id: template.series_id,
  code: template.code,
  language: template.language,
  version: template.version,
  status: template.status,
  organization_id: template.organization_id,
  source_kind: template.source_kind,
  derived_from_kind: template.derived_from_kind,
  derived_from_id: template.derived_from_id,
  type: template.type,
  name: template.name,
  tab_title: template.tab_title,
  description: template.description,
  sections_count: template.sections.length,
  catalog_published: template.catalog_published,
  created_at: template.created_at,
  updated_at: template.updated_at,
  is_system: template.is_system,
  created_by: template.created_by,
  author_display_name: template.author_display_name,
  updated_by: template.updated_by,
  updated_by_display_name: template.updated_by_display_name,
});

const upsertSummary = (
  summaries: DocumentTemplateSummary[],
  next: DocumentTemplateSummary,
): DocumentTemplateSummary[] => {
  const exists = summaries.some((t) => t.id === next.id);
  if (!exists) {
    return [...summaries, next];
  }
  return summaries.map((t) => (t.id === next.id ? next : t));
};

const setStatusById = (
  state: TemplatesState,
  id: string,
  status: VersionStatus,
) => {
  const summaryIndex = state.documentTemplateSummaries.findIndex(
    (t) => t.id === id,
  );
  if (summaryIndex !== -1) {
    state.documentTemplateSummaries[summaryIndex] = {
      ...state.documentTemplateSummaries[summaryIndex],
      status,
    };
  }
  const full = state.documentTemplateById[id];
  if (full) {
    state.documentTemplateById[id] = { ...full, status };
  }
};

const templatesSlice = createSlice({
  name: SliceName.templates,
  initialState,
  reducers: {
    // === Document Template actions (kept for optimistic updates) ===

    deleteDocumentTemplate: (state, action: PayloadAction<string>) => {
      setStatusById(state, action.payload, 'deleted');
    },

    // === Encounter Schema actions (kept for optimistic updates) ===

    setEncounterSchemas: (state, action: PayloadAction<EncounterSchema[]>) => {
      state.encounterSchemas = action.payload;
    },
    setEncounterSchemasLoading: (state, action: PayloadAction<boolean>) => {
      state.encounterSchemasLoading = action.payload;
    },
    addEncounterSchema: (state, action: PayloadAction<EncounterSchema>) => {
      state.encounterSchemas.push(action.payload);
    },
    updateEncounterSchema: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<EncounterSchema>;
      }>,
    ) => {
      const { id, updates } = action.payload;
      const index = state.encounterSchemas.findIndex((s) => s.id === id);
      if (index !== -1) {
        state.encounterSchemas[index] = {
          ...state.encounterSchemas[index],
          ...updates,
        };
      }
    },
    deleteEncounterSchema: (state, action: PayloadAction<string>) => {
      const index = state.encounterSchemas.findIndex(
        (s) => s.id === action.payload,
      );
      if (index !== -1) {
        state.encounterSchemas[index] = {
          ...state.encounterSchemas[index],
          status: 'deleted',
        };
      }
    },
    addDocumentToSchema: (
      state,
      action: PayloadAction<{
        schemaId: string;
        documentRef: SchemaDocumentRef;
      }>,
    ) => {
      const { schemaId, documentRef } = action.payload;
      const index = state.encounterSchemas.findIndex((s) => s.id === schemaId);
      if (index !== -1) {
        const schema = state.encounterSchemas[index];
        const alreadyExists = schema.documents.some(
          (d) => d.code === documentRef.code,
        );
        if (!alreadyExists) {
          state.encounterSchemas[index] = {
            ...schema,
            documents: [...schema.documents, documentRef],
          };
        }
      }
    },

    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // === Document Templates ===

    builder
      .addCase(fetchDocumentTemplates.pending, (state) => {
        state.documentTemplatesLoading = true;
        state.error = null;
      })
      .addCase(fetchDocumentTemplates.fulfilled, (state, action) => {
        state.documentTemplatesLoading = false;
        const incoming = action.payload.document_templates;
        const incomingIds = new Set(incoming.map((t) => t.id));
        const keptDeleted = state.documentTemplateSummaries.filter(
          (t) => t.status === 'deleted' && !incomingIds.has(t.id),
        );
        state.documentTemplateSummaries = [...incoming, ...keptDeleted];
      })
      .addCase(fetchDocumentTemplates.rejected, (state, action) => {
        state.documentTemplatesLoading = false;
        state.error =
          action.error.message ?? 'Failed to fetch document templates';
      });

    builder.addCase(
      fetchDeletedDocumentTemplates.fulfilled,
      (state, action) => {
        const deleted = action.payload.document_templates;
        const nonDeleted = state.documentTemplateSummaries.filter(
          (t) => t.status !== 'deleted',
        );
        state.documentTemplateSummaries = [...nonDeleted, ...deleted];
      },
    );

    builder
      .addCase(fetchDocumentTemplateById.pending, (state, action) => {
        state.documentTemplateByIdLoading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(fetchDocumentTemplateById.fulfilled, (state, action) => {
        const template = action.payload;
        state.documentTemplateByIdLoading[template.id] = false;
        state.documentTemplateById[template.id] = template;
        state.documentTemplateSummaries = upsertSummary(
          state.documentTemplateSummaries,
          toSummary(template),
        );
      })
      .addCase(fetchDocumentTemplateById.rejected, (state, action) => {
        state.documentTemplateByIdLoading[action.meta.arg] = false;
        state.error =
          action.error.message ?? 'Failed to fetch document template';
      });

    builder.addCase(
      createDocumentTemplateVersion.fulfilled,
      (state, action) => {
        const newTemplate = action.payload;

        // Mirrors scribe: a draft supersedes the previous draft of its series,
        // an active version supersedes the previous active version and every
        // draft it was published from, so the list matches the backend without
        // a refetch.
        if (newTemplate.series_id) {
          const isSuperseded = (
            t: Pick<DocumentTemplateSummary, 'id' | 'series_id' | 'status'>,
          ) =>
            t.id !== newTemplate.id &&
            t.series_id === newTemplate.series_id &&
            (t.status === newTemplate.status ||
              (newTemplate.status === 'active' && t.status === 'draft'));

          state.documentTemplateSummaries = state.documentTemplateSummaries.map(
            (t) => (isSuperseded(t) ? { ...t, status: 'archived' } : t),
          );
          Object.entries(state.documentTemplateById).forEach(([id, full]) => {
            if (isSuperseded(full)) {
              state.documentTemplateById[id] = { ...full, status: 'archived' };
            }
          });
        }

        state.documentTemplateById[newTemplate.id] = newTemplate;
        state.documentTemplateSummaries = upsertSummary(
          state.documentTemplateSummaries,
          toSummary(newTemplate),
        );
      },
    );

    builder.addCase(updateDocumentTemplateDraft.fulfilled, (state, action) => {
      const draft = action.payload;

      state.documentTemplateById[draft.id] = draft;
      state.documentTemplateSummaries = upsertSummary(
        state.documentTemplateSummaries,
        toSummary(draft),
      );
    });

    builder.addCase(deleteDocumentTemplateThunk.fulfilled, (state, action) => {
      setStatusById(state, action.payload, 'deleted');
    });

    builder.addCase(restoreDocumentTemplateThunk.fulfilled, (state, action) => {
      setStatusById(state, action.payload, 'active');
    });

    // === Encounter Schemas ===

    builder
      .addCase(fetchEncounterSchemas.pending, (state) => {
        state.encounterSchemasLoading = true;
        state.error = null;
        state.encounterSchemasListError = null;
      })
      .addCase(fetchEncounterSchemas.fulfilled, (state, action) => {
        state.encounterSchemasLoading = false;
        state.encounterSchemas = action.payload.schemas;
        state.encounterSchemasListError = null;
      })
      .addCase(fetchEncounterSchemas.rejected, (state, action) => {
        state.encounterSchemasLoading = false;
        const errorMessage =
          action.error.message ?? 'Failed to fetch encounter schemas';

        state.encounterSchemasListError = errorMessage;
        state.error = errorMessage;
      });

    builder.addCase(createEncounterSchemaThunk.fulfilled, (state, action) => {
      state.encounterSchemas.push(action.payload);
    });

    builder.addCase(updateEncounterSchemaThunk.fulfilled, (state, action) => {
      const updated = action.payload;
      const index = state.encounterSchemas.findIndex(
        (s) => s.id === updated.id,
      );
      if (index !== -1) {
        state.encounterSchemas[index] = updated;
      } else {
        state.encounterSchemas.push(updated);
      }
    });

    builder
      .addCase(fetchEncounterSchemaById.pending, (state) => {
        state.encounterSchemasLoading = true;
        state.error = null;
      })
      .addCase(fetchEncounterSchemaById.fulfilled, (state, action) => {
        state.encounterSchemasLoading = false;

        const schema = action.payload;
        const index = state.encounterSchemas.findIndex(
          (s) => s.id === schema.id,
        );
        if (index !== -1) {
          state.encounterSchemas[index] = schema;
        } else {
          state.encounterSchemas.push(schema);
        }
      })
      .addCase(fetchEncounterSchemaById.rejected, (state, action) => {
        state.encounterSchemasLoading = false;
        state.error =
          action.error.message ?? 'Failed to fetch encounter schema';
      });

    builder.addCase(deleteEncounterSchemaThunk.fulfilled, (state, action) => {
      const id = action.payload;
      const index = state.encounterSchemas.findIndex((s) => s.id === id);
      if (index !== -1) {
        state.encounterSchemas[index] = {
          ...state.encounterSchemas[index],
          status: 'deleted',
        };
      }
    });

    builder.addCase(restoreEncounterSchemaThunk.fulfilled, (state, action) => {
      const id = action.payload;
      const index = state.encounterSchemas.findIndex((s) => s.id === id);
      if (index !== -1) {
        state.encounterSchemas[index] = {
          ...state.encounterSchemas[index],
          status: 'active',
        };
      }
    });
  },
});

export const { actions } = templatesSlice;

export default templatesSlice.reducer;
